import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Play, Lock, Trash2 } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { mediaUrl, fmtDur } from "../config";
import Player from "../components/Player";
import GatedPlayer, { LockedOverlay } from "../components/GatedPlayer";
import { PaywallModal, SupportButton } from "../components/Payments";
import { SeriesCard, Avatar, Spinner, Empty, SectionTitle } from "../components/cards";
import { orderFeed } from "../subscriptions";

const gbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;

export const SeriesList = () => {
  const { user } = useAuth();
  const [series, setSeries] = useState(null);
  useEffect(() => {
    api.get("/series?limit=60").then((r) => setSeries(Array.isArray(r) ? r : r.series || [])).catch(() => setSeries([]));
  }, []);

  const feed = useMemo(() => (series ? orderFeed(series, user) : null), [series, user]);
  if (series === null || feed === null) return <Spinner />;
  if (series.length === 0) return <Empty>No series yet.</Empty>;
  const list = [...(feed.fromSubs || []), ...(feed.rest || [])];

  return (
    <div className="mx-auto max-w-7xl" data-testid="series-page">
      <h1 className="mb-6 font-heading text-2xl font-bold tracking-tight text-white">Series</h1>
      {feed.personalized && feed.fromSubs.length > 0 && (
        <section className="mb-8" data-testid="series-from-subscriptions">
          <SectionTitle>From your subscriptions</SectionTitle>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 xl:grid-cols-5">
            {feed.fromSubs.map((s) => (
              <SeriesCard key={s.id} series={s} />
            ))}
          </div>
        </section>
      )}
      {feed.personalized && <SectionTitle>More series</SectionTitle>}
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 xl:grid-cols-5" data-testid="series-grid">
        {(feed.personalized ? feed.rest : list).map((s) => (
          <SeriesCard key={s.id} series={s} />
        ))}
      </div>
      <p className="mt-6 text-xs text-slate-600">All series episodes are free to watch on the web.</p>
    </div>
  );
};

export const SeriesDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(null);
  const [access, setAccess] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [locked, setLocked] = useState(false);
  const [season, setSeason] = useState(1);
  const [delNote, setDelNote] = useState("");

  const loadAccess = () =>
    api.get(`/series/${id}/pricing`)
      .then((p) => setAccess({
        paid: !!p.paid,
        price: p.price,
        preview_minutes: p.previewMinutes ?? 10,
        show_ads: p.adsMode !== "noads",
        owned: !!p.owned,
        is_creator: !!p.isOwner,
        sales: p.sales || null,
      }))
      .catch(() => setAccess({ paid: false, owned: true }));

  useEffect(() => {
    setData(null);
    setCurrent(null);
    setAccess(null);
    setLocked(false);
    setShowPaywall(false);
    setDelNote("");
    api.get(`/series/${id}`).then((r) => {
      const series = r.series ?? r;
      const episodes = r.episodes ?? series.episodes ?? [];
      setData({ ...series, episodes });
      if (episodes[0]) setCurrent(episodes[0]);
    }).catch(() => setData({ episodes: [] }));
    loadAccess();
  }, [id]);

  useEffect(() => {
    setData(null);
    setCurrent(null);
    setAccess(null);
    setLocked(false);
    setShowPaywall(false);
    setDelNote("");
    api.get(`/series/${id}`).then((r) => {
      const series = r.series ?? r;
      const episodes = r.episodes ?? series.episodes ?? [];
      setData({ ...series, episodes });
      if (episodes[0]) setCurrent(episodes[0]);
    }).catch(() => setData({ episodes: [] }));
    loadAccess();
  }, [id]);

  if (data === null || access === null) return <Spinner />;

  const episodes = [...(data.episodes || [])].sort(
    (a, b) => (a.seasonNumber ?? 1) - (b.seasonNumber ?? 1) || (a.episodeNumber ?? 0) - (b.episodeNumber ?? 0)
  );
  const seasons = [...new Set(episodes.map((e) => e.seasonNumber ?? 1))].sort((a, b) => a - b);
  const seasonEps = episodes.filter((e) => (e.seasonNumber ?? 1) === season);

  const isCreator = access.is_creator;
  const sales = access.sales;
  const isLocked = access.paid && !access.owned; // viewer who hasn't bought
  const currentLocked = isLocked && !!current?.lock; // this episode is locked for this viewer
  const previewMin = access.preview_minutes ?? 10;
  const cover = mediaUrl(data.cover || data.coverImage || data.thumbnail);

  const pickEpisode = (ep) => {
    setCurrent(ep);
    setLocked(false);
  };

  const deleteEpisode = async (ep) => {
    setDelNote("");
    try {
      await api.del(`/series/${id}/episodes/${ep.id}`);
      setData((d) => ({ ...d, episodes: d.episodes.filter((e) => e.id !== ep.id) }));
      if (current?.id === ep.id) setCurrent(null);
    } catch {
      setDelNote("Episode deletion isn't available yet — it needs an app-server update.");
    }
  };

  return (
    <div className="mx-auto max-w-5xl" data-testid="series-detail-page">
      {/* Header: cover, title, creator · episodes, description */}
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
        {cover && (
          <div className="relative h-40 w-full sm:h-52">
            <img src={cover} alt={data.title} className="h-full w-full object-cover" data-testid="series-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07070A] to-transparent" />
          </div>
        )}
        <div className="p-5">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-white" data-testid="series-title">{data.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            {data.creator && (
              <Link to={`/app/creator/${data.creator.id || data.creatorId}`} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="series-creator-line">
                <Avatar user={data.creator} size={24} />
                {data.creator.displayName || data.creator.username} · {episodes.length} episode{episodes.length === 1 ? "" : "s"}
              </Link>
            )}
            {data.creator && <SupportButton target={{ id, title: data.title }} creator={data.creator} />}
          </div>
          {access.paid && (
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-crimson/40 bg-crimson/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-crimson" data-testid="series-paid-badge">
              <Lock size={11} /> Paid series · {gbp(access.price)}
            </p>
          )}
          {data.description && <p className="mt-3 text-sm leading-relaxed text-slate-400" data-testid="series-description">{data.description}</p>}
        </div>
      </div>

      {/* Creator: Paid series card */}
      {isCreator && (
        <PaidSeriesCard
          seriesId={id}
          access={access}
          sales={sales}
          onSaved={() => { loadAccess(); }}
        />
      )}

      {/* Player */}
      {current && (
        <div className="mt-6">
          <div className="relative">
            {currentLocked ? (
              <>
                <GatedPlayer video={current} className="aspect-video w-full rounded-2xl" onLocked={() => setLocked(true)} />
                {locked && <LockedOverlay title={data.title} />}
              </>
            ) : (
              <Player video={current} className="aspect-video w-full rounded-2xl" />
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="font-heading text-sm font-semibold text-white" data-testid="now-playing-episode">
              {current.seasonNumber != null ? `S${current.seasonNumber} · E${current.episodeNumber} — ` : ""}{current.title}
            </p>
            {isLocked && (
              <button onClick={() => setShowPaywall(true)} data-testid="series-unlock-button" className="inline-flex items-center gap-2 rounded-full bg-crimson px-5 py-2.5 font-heading text-xs font-bold text-white transition-colors hover:bg-crimson-hover">
                <Lock size={13} /> Pay {gbp(access.price)} to continue
              </button>
            )}
          </div>
          {currentLocked && !locked && (
            <p className="mt-2 text-xs text-slate-500" data-testid="preview-note">Free preview: first {previewMin} minute{previewMin === 1 ? "" : "s"} of this series.</p>
          )}
        </div>
      )}

      {/* Season tabs */}
      {seasons.length > 0 && (
        <div className="mt-8 flex gap-2 overflow-x-auto pb-1" data-testid="season-tabs">
          {seasons.map((s) => (
            <button
              key={s}
              onClick={() => setSeason(s)}
              data-testid={`season-tab-${s}`}
              className={`shrink-0 rounded-lg px-4 py-2 font-heading text-sm font-semibold transition-colors ${season === s ? "bg-white text-black" : "bg-white/[0.08] text-slate-200 hover:bg-white/[0.16]"}`}
            >
              Season {s}
            </button>
          ))}
        </div>
      )}

      {/* Episode list */}
      {delNote && <p className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-slate-300" data-testid="episode-delete-note">{delNote}</p>}
      <div className="mt-4 space-y-2">
        {seasonEps.length === 0 ? (
          <Empty>No episodes in this season yet.</Empty>
        ) : (
          seasonEps.map((ep) => (
            <div
              key={ep.id}
              className={`flex w-full items-center gap-4 rounded-xl border p-3 text-left transition-colors ${current?.id === ep.id ? "border-crimson/50 bg-crimson/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
              data-testid={`episode-${ep.id}`}
            >
              <button onClick={() => pickEpisode(ep)} className="relative h-16 w-28 shrink-0 overflow-hidden rounded-lg bg-surface">
                {mediaUrl(ep.thumbnail) && <img src={mediaUrl(ep.thumbnail)} alt="" className="h-full w-full object-cover" />}
                <span className="absolute left-1.5 top-1.5 rounded-md bg-black/80 px-1.5 py-0.5 font-mono text-[10px] font-bold text-white">E{ep.episodeNumber ?? ""}</span>
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  {isLocked && ep.lock ? <Lock size={16} className="text-crimson" /> : <Play size={16} className="text-white" fill="white" />}
                </div>
              </button>
              <button onClick={() => pickEpisode(ep)} className="min-w-0 flex-1 text-left">
                <p className="truncate font-heading text-sm font-semibold text-white">{ep.title}</p>
                {ep.durationSec > 0 && <p className="font-mono text-[10px] text-slate-500">{fmtDur(ep.durationSec)}</p>}
              </button>
              {isCreator && (
                <button onClick={() => deleteEpisode(ep)} title="Delete episode" data-testid={`delete-episode-${ep.id}`} className="rounded-full p-2 text-slate-500 transition-colors hover:bg-crimson/15 hover:text-crimson">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {showPaywall && (
        <PaywallModal
          series={data}
          access={access}
          onClose={() => setShowPaywall(false)}
          onOwned={() => {
            setShowPaywall(false);
            setLocked(false);
            // re-fetch both pricing (owned=true now) and episodes (locks cleared)
            loadAccess();
            api.get(`/series/${id}`).then((r) => {
              const series = r.series ?? r;
              const episodes = r.episodes ?? series.episodes ?? [];
              setData({ ...series, episodes });
            }).catch(() => {});
          }}
        />
      )}
    </div>
  );
};

// Creator's inline "Paid series" card: on/off switch, buyers/earned, price,
// free-preview stepper, ads choice, Save. Writes straight to the app server
// (PUT /series/{id}/pricing) so settings sync with the mobile app.
const PaidSeriesCard = ({ seriesId, access, sales, onSaved }) => {
  const [on, setOn] = useState(!!access.paid);
  const [price, setPrice] = useState(access.paid ? String(access.price) : "");
  const [previewMin, setPreviewMin] = useState(access.paid ? (access.preview_minutes ?? 10) : 10);
  const [showAds, setShowAds] = useState(access.show_ads !== false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const save = async () => {
    const p = parseFloat(price);
    setBusy(true);
    setNote(null);
    try {
      await api.put(`/series/${seriesId}/pricing`, {
        paid: true,
        price: p,
        previewMinutes: previewMin,
        adsMode: showAds ? "ads" : "noads",
      });
      setNote({ tone: "ok", text: "Saved." });
      onSaved();
    } catch (e) {
      setNote({ tone: "err", text: e.message || "Could not save." });
    }
    setBusy(false);
  };

  const toggle = async () => {
    if (on) {
      // switching off -> make the series free again
      setBusy(true);
      try {
        await api.put(`/series/${seriesId}/pricing`, { paid: false });
        setOn(false);
        onSaved();
      } catch {}
      setBusy(false);
    } else {
      setOn(true);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="paid-series-card">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-base font-bold text-white">Paid series</h2>
          <p className="mt-1 text-xs text-slate-500" data-testid="paid-series-stats">
            {sales ? sales.buyers : 0} buyer{(sales ? sales.buyers : 0) === 1 ? "" : "s"} · earned {gbp(sales ? sales.earned : 0)}
          </p>
        </div>
        <button
          onClick={toggle}
          disabled={busy}
          role="switch"
          aria-checked={on}
          data-testid="paid-series-switch"
          className={`relative h-7 w-12 rounded-full transition-colors ${on ? "bg-crimson" : "bg-white/15"}`}
        >
          <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>

      {on && (
        <div className="mt-5 space-y-4">
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">One-time price (£0.50 – £100)</label>
            <div className="relative max-w-xs">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">£</span>
              <input type="number" min="0.5" max="100" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="4.99" data-testid="paid-series-price" className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-3 pl-8 pr-4 text-sm text-white outline-none focus:border-crimson/60" />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Free preview per episode</label>
            <div className="flex items-center gap-3">
              <button onClick={() => setPreviewMin((m) => Math.max(1, m - 1))} data-testid="preview-minus" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-lg text-slate-300 transition-colors hover:border-crimson/50">−</button>
              <span className="min-w-[64px] text-center font-heading text-sm font-bold text-white" data-testid="preview-length">{previewMin} min</span>
              <button onClick={() => setPreviewMin((m) => Math.min(10, m + 1))} data-testid="preview-plus" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-lg text-slate-300 transition-colors hover:border-crimson/50">+</button>
            </div>
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Ads on this series</label>
            <div className="flex max-w-xs gap-2">
              <button onClick={() => setShowAds(true)} data-testid="ads-show" className={`flex-1 rounded-xl border px-4 py-2.5 font-heading text-xs font-bold transition-colors ${showAds ? "border-crimson bg-crimson/15 text-white" : "border-white/15 text-slate-400 hover:border-white/30"}`}>Show ads</button>
              <button onClick={() => setShowAds(false)} data-testid="ads-none" className={`flex-1 rounded-xl border px-4 py-2.5 font-heading text-xs font-bold transition-colors ${!showAds ? "border-crimson bg-crimson/15 text-white" : "border-white/15 text-slate-400 hover:border-white/30"}`}>No ads</button>
            </div>
            {!showAds && (
              <p className="mt-2 text-xs text-amber-400" data-testid="noads-line">The app takes 40% of sales, you keep 60%. Views earn no revenue per view.</p>
            )}
          </div>
          {note && <p className={`text-sm ${note.tone === "ok" ? "text-emerald-400" : "text-crimson"}`} data-testid="paid-series-note">{note.text}</p>}
          <button onClick={save} disabled={busy} data-testid="paid-series-save" className="rounded-full bg-crimson px-6 py-2.5 font-heading text-xs font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
};
